/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { LineChart } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { MUENSTER_DEVICE_SENSOR_CONFIG, NEUTRAUBLING_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import { PLOTLY_MIN_WIDTH } from '@/config/app';
import {
    createPvMonthlyLossBarsRequest,
    getPlotlyRequestMeter,
} from '@/config/eeCheckConfig';
import { createPvPredictionDetailKpiConfigs } from '@/config/kpi/detailPhotovoltaics';

/* Import components */
import Api2StoredPlotlyPlot from '@/components/api/Api2StoredPlotlyPlot';
import Api2TimeSeriesPlot from '@/components/api/Api2TimeSeriesPlot';
import KpiValueApi2TimeSeries from '@/components/api/KpiValueApi2TimeSeries';
import DashboardGrid from '@/components/layout/DashboardGrid';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { getSensorHistoryAxisRange } from '@/lib/timeSeries/sensorHistoryAxisRange';

function PvPredictionDetailTab() {
    const { locale, t } = useI18n();
    const { pvKey } = useParams<{ pvKey: string }>();
    const [selectedChart, setSelectedChart] = useState<
        'sensor-history-3months' | 'sensor-history-12months'
    >('sensor-history-3months');

    const pvPredictionDeviceSensorCatalog = new DeviceSensorCatalog(pvKey?.startsWith('Mue') ? MUENSTER_DEVICE_SENSOR_CONFIG : NEUTRAUBLING_DEVICE_SENSOR_CONFIG);
    const pvPredictionSystemKeys = pvPredictionDeviceSensorCatalog.getVirtualDevices('PV');
    const selectedPvKey = pvKey && pvPredictionSystemKeys.includes(pvKey) ? pvKey : null;
    const selectedPvDeviceSensor = selectedPvKey ? new DeviceSensor(selectedPvKey, pvPredictionDeviceSensorCatalog) : null;

    if (!selectedPvKey || !selectedPvDeviceSensor) {
        return <Navigate to={APP_ROUTES.pvprediction} replace />;
    }

    const pvSystemName = selectedPvDeviceSensor.getDeviceName() ?? selectedPvKey;
    const sensorHistoryMonths = selectedChart === 'sensor-history-3months' ? 3 : 12;
    const pvMonthlyLossSensorName = getPlotlyRequestMeter(
        selectedPvKey.startsWith('Mue.') ? 'muenster' : 'neutraubling',
        selectedPvKey,
        'pv_plots',
        '',
    );
    const pvMonthlyLossRequest = createPvMonthlyLossBarsRequest(pvMonthlyLossSensorName);

    /* time series */
    const measuredTimeSeriesSensorName = selectedPvDeviceSensor.getSensorName(null, 'P_15m');

    if (!measuredTimeSeriesSensorName) {
        return <Navigate to={APP_ROUTES.pvprediction} replace />;
    }

    const kpiConfigs = createPvPredictionDetailKpiConfigs({
        deviceSensor: selectedPvDeviceSensor,
        t: t,
    });

    if (kpiConfigs === null) {
        return <Navigate to={APP_ROUTES.pvprediction} replace />;
    }

    const outageComparisonDate = new Date();
    outageComparisonDate.setDate(outageComparisonDate.getDate() - 30);
    const outageComparisonMetaText = t('Comparison value from {date}', {
        date: outageComparisonDate.toLocaleDateString(locale),
    });

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 text-sm font-medium text-base-content/75">
                <span>
                    <strong>{pvSystemName}:</strong> {selectedPvKey}
                </span>
                <div className="chart-selection" role="group" aria-label={t('Chart selection')}>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-3months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-3months'}
                        title={t('Show measured PV power history for 3 months')}
                        onClick={() => setSelectedChart('sensor-history-3months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Measured PV power history (3 months)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-12months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-12months'}
                        title={t('Show measured PV power history for 12 months')}
                        onClick={() => setSelectedChart('sensor-history-12months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Measured PV power history (12 months)')}
                    </button>
                </div>
            </div>
            <DashboardGrid className="min-h-0 flex-1" desktopCols={3} desktopRows={['150px', '1fr', '1fr']} breakpoint={'2xl'} aria-label={t('PV prediction dashboard')}>
                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.outage}
                        metaText={outageComparisonMetaText}
                    />
                </DashboardGrid.Item>
                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.redispatch}
                        metaText={outageComparisonMetaText}
                    />
                </DashboardGrid.Item>
                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.yearlyLoss}
                    />
                </DashboardGrid.Item>

                {/* Scrollable panels require a matching dashboard path in src/config/appPageLayout.ts. */}
                <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={3} mobileMinHeight={'750px'} scrollable>
                    <Api2TimeSeriesPlot
                        from={sensorHistoryMonths === 3 ? 'NOW-3MONTHS' : 'NOW-12MONTHS'}
                        to="NOW"
                        urn={measuredTimeSeriesSensorName}
                        includeZeroYAxis
                        legendNames={{
                            [measuredTimeSeriesSensorName]: t('Measured PV power {key}', { key: selectedPvKey }),
                        }}
                        traceColors={{
                            [measuredTimeSeriesSensorName]: '#6692b1',
                        }}
                        minWidth={PLOTLY_MIN_WIDTH}
                        title={t('Measured PV power {key}', { key: selectedPvKey })}
                        xAxisTitle={t('Period')}
                        xAxisRange={getSensorHistoryAxisRange(sensorHistoryMonths)}
                        yAxisTitle={t('PV power in kW')}
                    />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={3}>
                    <Api2StoredPlotlyPlot
                        request={pvMonthlyLossRequest}
                        textOverrides={{
                            legendTitle: t('Series'),
                            traceNames: {
                                Measurement: t('Measurement'),
                                Prediction: t('Prediction'),
                            },
                            xAxisTitle: t('Month'),
                            yAxisTitle: t('Monthly energy in Wh'),
                        }}
                    />
                </DashboardGrid.Item>
            </DashboardGrid>
        </div>
    );
}

export default PvPredictionDetailTab;
